import type { FieldValues } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { FormField, type FormFieldProps } from '~/chords';
import { Toggle, type ToggleProps } from '~/chords';
import type { ComponentProps } from '~/chords/ComponentProps';
import { FieldMessage } from '~/chords/FieldMessage';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import { cn } from '~/chords/lib/cn';

type FormFieldToggleProps<TFormFields extends FieldValues> = ComponentProps<
  Omit<FormFieldProps<TFormFields>, 'children'> &
    Omit<ToggleProps, 'name' | 'className' | 'variant'> & {
      layout?: 'vertical' | 'horizontal';
    }
>;

export function FormFieldToggle<TFormFields extends FieldValues>({
  name,
  label,
  dangerouslyOverrideClassName,
  disabled,
  readOnly,
  layout = 'vertical',
  ...toggleProps
}: FormFieldToggleProps<TFormFields>) {
  const {
    field,
    fieldState: { error },
  } = useController({ name, disabled });

  if (layout === 'horizontal') {
    return (
      <div
        className={cn(
          'flex flex-col gap-sm',
          getClassNameOverride(
            { ...dangerouslyOverrideClassName },
            'FormFieldToggle',
          ),
        )}
      >
        <div className="flex items-center justify-between">
          <label
            htmlFor={name}
            className={cn(
              'text-body-md',
              (disabled || readOnly) && 'text-subtle',
            )}
          >
            {label}
          </label>
          <Toggle
            {...field}
            id={name}
            pressed={field.value}
            onPressedChange={field.onChange}
            {...toggleProps}
            disabled={disabled || readOnly}
          />
        </div>
        {error ? (
          <FieldMessage state="invalid">{error.message}</FieldMessage>
        ) : null}
      </div>
    );
  }

  return (
    <FormField
      name={name}
      label={label}
      dangerouslyOverrideClassName={dangerouslyOverrideClassName}
      disabled={disabled}
      readOnly={readOnly}
    >
      <Toggle
        id={name}
        aria-label={label}
        {...field}
        pressed={field.value}
        onPressedChange={field.onChange}
        variant="formField"
        {...toggleProps}
        disabled={disabled || readOnly}
      />
    </FormField>
  );
}

FormFieldToggle.displayName = 'FormFieldToggle';
